import React, { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

interface TesterRow {
  id: string;
  email: string;
  submittedAt?: Timestamp;
  checkboxAccepted?: boolean;
  added?: boolean;
  addedAt?: Timestamp;
}

function formatDate(ts?: Timestamp): string {
  if (!ts) return '-';
  try {
    return ts.toDate().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
}

const AndroidTesters: React.FC = () => {
  const [rows, setRows] = useState<TesterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'androidTesters'), orderBy('submittedAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<TesterRow, 'id'>),
        }));
        setRows(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading android testers:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  const handleMarkAdded = async (id: string) => {
    setSavingId(id);
    try {
      await updateDoc(doc(db, 'androidTesters', id), {
        added: true,
        addedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Failed to mark tester as added:', err);
      alert('Could not mark as added. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  const pendingCount = rows.filter((r) => r.added !== true).length;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Android Testers</h1>
        <span className="text-sm text-gray-500">
          {pendingCount} not yet added · {rows.length} total
        </span>
      </div>

      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-gray-500">No tester signups yet.</div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-gray-600">
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Signed Up</th>
                <th className="px-4 py-3 font-medium">Consent</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isAdded = row.added === true;
                return (
                  <tr
                    key={row.id}
                    className={`border-t border-gray-200 ${isAdded ? 'bg-gray-50' : ''}`}
                  >
                    <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                      {row.email || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {formatDate(row.submittedAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {row.checkboxAccepted ? (
                        <span className="text-green-600">Accepted</span>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {isAdded ? (
                        <span className="text-gray-500">
                          Added {formatDate(row.addedAt)}
                        </span>
                      ) : (
                        <span className="text-red-500">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isAdded ? (
                        <span className="inline-flex items-center rounded px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-400 cursor-default">
                          Added ✓
                        </span>
                      ) : (
                        <button
                          onClick={() => handleMarkAdded(row.id)}
                          disabled={savingId === row.id}
                          className="inline-flex items-center rounded px-3 py-1.5 text-xs font-medium bg-[#00e3ff] text-black hover:opacity-80 transition disabled:opacity-50"
                        >
                          {savingId === row.id ? 'Saving…' : 'Added'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AndroidTesters;
